<template>
  <div class="document-container">
    <es-exception
      v-if="loadingSummaryError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."/>
    <template v-else>
      <cep-manager-recalculate-banner
        ref="banner"
        :hasRefresh="!electionsPermissions.viewOnly && schoolsCount === 0"
        :hasAccess="electionsPermissions.canAccess"
        :loadingSummary="loadingSummary"
        :cepSummary="cepSummary"
        @refresh="refresh"/>
      <div class="columns">
        <div class="column is-10">
          <cep-manager-recalculate-calculate-worksheet
            ref="calculateWorksheet"
            :cepSummary="cepSummary"
            :locationGroupId="locationGroupId"
            :schoolYear="schoolYear"
            :sortField="sortField"
            :sortOrder="sortOrder"
            :electionsPermissions="electionsPermissions"
            @schoolsChanged="onSchoolsChanged($event)"
            @refresh="refresh"
            @electionsScopeChanged="updateElectionsScope"
            @participationDecisionChanged="participationDecisionChanged"/>
        </div>
        <div class="column is-2">
          <cep-manager-recalculate-group-summary
            ref="groupSummary"
            :locationGroupId="locationGroupId"
            :schoolYear="schoolYear"/>
        </div>
      </div>
      <cep-manager-recalculate-participation-summary
        ref="participationSummary"
        v-if="electionsPermissions.canAccess"
        :loadingSummary="loadingSummary"
        :locationGroupId="locationGroupId"
        :schoolYear="schoolYear"
        :sortField="sortField"
        :sortOrder="sortOrder"
        :electionsPermissions="electionsPermissions"
        :cepSummary="cepSummary"
        @baseYearDecisionChanged="baseYearDecisionChanged"/>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import CepManagerRecalculateCalculateWorksheet from '@/components/CepManagerRecalculateCalculateWorksheet.vue';
import CepManagerRecalculateGroupSummary from '@/components/CepManagerRecalculateGroupSummary.vue';
import CepManagerRecalculateBanner from '@/components/CepManagerRecalculateBanner.vue';
import CepManagerRecalculateParticipationSummary from '@/components/CepManagerRecalculateParticipationSummary.vue';
import EsException from '@/components/EsException.vue';
import NotificationManager from '@/mixins/NotificationManager';

export default {
  mixins: [
    NotificationManager,
  ],
  components: {
    CepManagerRecalculateCalculateWorksheet,
    CepManagerRecalculateGroupSummary,
    CepManagerRecalculateBanner,
    CepManagerRecalculateParticipationSummary,
    EsException,
  },
  data() {
    return {
      schoolsCount: -1,
    };
  },
  props: {
    cepSummary: {
      type: Object,
    },
    locationGroupId: {
      type: [String, Number],
    },
    schoolYear: {
      type: Number,
    },
    sortField: {
      type: String,
      default: 'locationName',
    },
    sortOrder: {
      type: String,
      default: 'ASC',
    },
    electionsPermissions: {
      type: Object,
    },
    loadingSummary: {
      type: Boolean,
    },
    loadingSummaryError: {
      type: Boolean,
    },
  },
  methods: {
    async refresh() {
      try {
        this.$refs.banner.setBusy(true);
        this.setBusy(true);
        if (this.cepSummary.recalculateStatus === 'N' || this.cepSummary.recalculateStatus === 'E') {
          await axios.post(`cep/${this.locationGroupId}/elections/refresh`, {});
        }
        this.$emit('change');
        this.onLoad();
      } catch (error) {
        this.$refs.banner.setBusy(false);
        this.handleError(error, 'There was a problem refreshing recalculate data. ');
      }
    },
    async updateElectionsScope(isDistrictWide) {
      try {
        this.setBusy(true);
        await axios.put(`cep/${this.locationGroupId}/elections/district-wide?enable=${isDistrictWide}`, {});
        this.$emit('change');
        this.onLoad();
      } catch (error) {
        this.handleError(error);
      }
    },
    async participationDecisionChanged(data) {
      try {
        this.setBusy(true);
        const { selectedCluster } = data;
        const { clusterId } = selectedCluster;
        let url = `cep/${this.locationGroupId}/elections/schools/${data.stateLocationId}/participation`;

        if (clusterId === 'ROY' || clusterId === 'Y' || clusterId === 'N') {
          url += `?status=${clusterId}`;
        } else {
          url += `?clusterId=${clusterId}&clusterNum=${selectedCluster.cluster.clusterNum}`;
        }

        await axios.put(url, {});
        this.showNotification('success', ['Election Decision has been changed.']);
      } catch (error) {
        this.handleError(error);
      } finally {
        this.onLoad(true);
      }
    },
    baseYearDecisionChanged() {
      this.$emit('change');
    },
    setBusy(value) {
      this.$refs.calculateWorksheet.setBusy(value);
      this.$refs.groupSummary.setBusy(value);
      this.$refs.participationSummary.setBusy(value);
    },
    onSchoolsChanged(data) {
      this.schoolsCount = data ? data.length : 0;
    },
    onLoad(loadAll) {
      this.$refs.calculateWorksheet.onLoad(loadAll);
      this.$refs.groupSummary.onLoad();
      this.$refs.participationSummary.onLoad();
    },
    handleError(error, msg = '') {
      const { status } = error.response;
      let apiMsg = '';

      if (status === 403) {
        apiMsg = 'Sorry, you are not authorized to access this content.';
      } else if (status === 400) {
        apiMsg = `${msg}${error.response.data.message}`;
      } else {
        apiMsg = 'There was an issue. Please try again or contact your administrator.';
      }

      this.showNotification('danger', [apiMsg]);
      this.setBusy(false);
    },
  },
};
</script>

<style lang="sass" scoped>
  .document-container
    padding-top: 0
</style>
